import { ResourceEnum } from "src/constants/constants.ts";
import { createModuleI18nNamespaces } from "src/system/createModuleI18nNamespaces.ts";
import { OrderTranslationNamespaceEnum } from "src/admin/modules/order/constants.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";

export const getTranslationsPath = (
  ns: string,
  lang: string,
): null | string => {
  if (
    ns ===
    createModuleI18nNamespaces({
      resource: ResourceEnum.B2bGateOrder,
    }).form({
      type: "edit",
    })
  ) {
    return `src/admin/modules/b2b-gate-order/components/EditSupplierOrderForB2bGate/locales/${lang}.json`;
  }

  if (ns === OrderTranslationNamespaceEnum.CreateOrder) {
    return `src/admin/modules/order/routes/create/create-order/create.general.${lang}.json`;
  }

  if (ns === OrderTranslationNamespaceEnum.ProductDataGrid) {
    return `src/admin/modules/order/routes/edit/tabs/general/components/OrderProductsDataGrid/${lang}.json`;
  }

  const resources = (
    Object.keys(ResourceEnum) as Array<keyof typeof ResourceEnum>
  ).map((key) => ResourceEnum[key]);

  const resourceTranslations = resources.find((resource) => {
    const namespace = getResourceI18nNamespace(resource);
    return namespace === ns;
  });

  if (resourceTranslations) {
    return `src/admin/translations/${resourceTranslations}/${lang}.json`;
  }

  const resourceList = resources.find((resource) => {
    const namespace = createModuleI18nNamespaces({
      resource,
    });
    return namespace.list() === ns;
  });
  if (resourceList) {
    return `src/admin/lists/${resourceList}/list.${lang}.json`;
  }

  const formResource = resources.find((resource) => {
    const namespace = createModuleI18nNamespaces({
      resource,
    });

    return (
      namespace.form({ type: "create" }) === ns ||
      namespace.form({ type: "edit" }) === ns
    );
  });

  if (formResource) {
    return `src/admin/forms/${formResource}/locales/${lang}.json`;
  }

  const layoutResource = resources.find((resource) => {
    const namespace = createModuleI18nNamespaces({
      resource,
    });

    return namespace.layout() === ns;
  });

  if (layoutResource) {
    return `src/admin/modules/${layoutResource}/locales/${lang}.json`;
  }

  const match = ns.match(/^module-(.+)-([^-]+)-([^-]+)$/);

  if (match) {
    const resource = match[1];
    const tab = match[3];

    return `src/admin/forms/${resource}/tabs/${tab}/locales/${lang}.json`;
  }

  // @ts-ignore
  if (namespaceToPathMap?.[ns]) {
    // @ts-ignore
    return namespaceToPathMap[ns](lang);
  }
  return null;
};

const namespaceToPathMap = {
  [TranslationNamespaceEnum.UnexpectedError]: (lang: string) =>
    `src/components/UnexpectedError/locales/${lang}.json`,
  [TranslationNamespaceEnum.AdminLayout]: (lang: string) =>
    `src/admin/layout/locales/${lang}.json`,
  [TranslationNamespaceEnum.Common]: (lang: string) =>
    `src/locales/${lang}/common.json`,

  [TranslationNamespaceEnum.DashboardPage]: (lang: string) =>
    `src/admin/dashboard/dashboard.${lang}.json`,
  [TranslationNamespaceEnum.NotFoundPage]: (lang: string) =>
    `src/admin/not-found/not-found.${lang}.json`,
  [TranslationNamespaceEnum.LoginPage]: (lang: string) =>
    `src/auth/pages/LoginPage/locales/${lang}/loginPage.json`,
  [TranslationNamespaceEnum.B2BLoginPage]: (lang: string) =>
    `src/auth/pages/LoginPageB2B/${lang}.json`,
  [TranslationNamespaceEnum.DataGridComponent]: (lang: string) =>
    `src/components/DataGrid/DataGrid.${lang}.json`,
  [TranslationNamespaceEnum.SelectV2Component]: (lang: string) =>
    `src/components/SelectV2/locales/${lang}/selectV2Component.json`,
  [TranslationNamespaceEnum.AutocompleteComponent]: (lang: string) =>
    `src/components/Autocomplete/autocomplete.${lang}.json`,
  [TranslationNamespaceEnum.ProTabsComponent]: (lang: string) =>
    `src/components/ProTabs/locales/${lang}/proTabsComponent.json`,
  [TranslationNamespaceEnum.PaginationV2Component]: (lang: string) =>
    `src/components/PaginationV2/locales/${lang}/paginationV2Component.json`,
  [TranslationNamespaceEnum.TextAreaComponent]: (lang: string) =>
    `src/components/TextArea/locales/${lang}/textAreaComponent.json`,
  [TranslationNamespaceEnum.ErrorBoundaryComponent]: (lang: string) =>
    `src/components/ErrorBoundary/locales/${lang}/errorBoundaryComponent.json`,
  [TranslationNamespaceEnum.FileInputComponent]: (lang: string) =>
    `src/components/FileInput/locales/${lang}/fileInputComponent.json`,
  [TranslationNamespaceEnum.DropZoneInputComponent]: (lang: string) =>
    `src/components/DropZoneInput/locales/${lang}/dropZoneInputComponent.json`,
  [TranslationNamespaceEnum.DropZoneInputComponentV2]: (lang: string) =>
    `src/components/DropZoneInputV2/locales/${lang}/dropZoneInputComponent.json`,
  [TranslationNamespaceEnum.ExonnHubComponent]: (lang: string) =>
    `src/admin/modules/b2b-gate-order/components/ExonnHub/locales/${lang}/exonnHubComponent.json`,
  [TranslationNamespaceEnum.TransferListComponent]: (lang: string) =>
    `src/components/TransferList/locales/${lang}/transferListComponent.json`,
  [TranslationNamespaceEnum.CommentsFormPanel]: (lang: string) =>
    `src/components/Form/panels/CommentsPanel/CommentsPanel.${lang}.json`,
  [TranslationNamespaceEnum.BillBalance]: (lang: string) =>
    `src/admin/bill/balance/locales/${lang}.json`,
  [TranslationNamespaceEnum.MarketplaceField]: (lang: string) =>
    `src/admin/forms/product/shared/MarketplaceField/locales/${lang}.json`,
  [TranslationNamespaceEnum.InternalServerError]: (lang: string) =>
    `src/components/QueryError/components/InternalServerError/locales/${lang}.json`,
  [TranslationNamespaceEnum.Bill]: (lang: string) =>
    `src/admin/bill/locales/${lang}.json`,
  [TranslationNamespaceEnum.FormComponent]: (lang: string) =>
    `src/components/Form/locales/${lang}.json`,
  [TranslationNamespaceEnum.TransferPanel]: (lang: string) =>
    `src/components/Form/panels/TransferPanel/TransferPanel.${lang}.json`,
  [TranslationNamespaceEnum.PricePanel]: (lang: string) =>
    `src/components/Form/panels/PricePanel/locales/${lang}.json`,
  [TranslationNamespaceEnum.Administration]: (lang: string) =>
    `src/admin/administration/locales/${lang}.json`,
  [TranslationNamespaceEnum.ManageAttributeValues]: (lang: string) =>
    `src/admin/modules/attribute/routes/edit/tabs/attribute-values/edit.attribute-values.${lang}.json`,
  [TranslationNamespaceEnum.AssignAttributesToVariant]: (lang: string) =>
    `src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/attributes/locales/${lang}.json`,
  [TranslationNamespaceEnum.DateRangeStatusCell]: (lang: string) =>
    `src/components/DateRangeStatusCell/${lang}.json`,
  [TranslationNamespaceEnum.SelectProductTypeModal]: (lang: string) =>
    `src/admin/modals/product/SelectProductType/locales/${lang}.json`,
  [TranslationNamespaceEnum.SelectPriceListsModal]: (lang: string) =>
    `src/admin/modals/product/SelectPriceLists/locales/${lang}.json`,
  [TranslationNamespaceEnum.AssignComponentsToSetModal]: (lang: string) =>
    `src/admin/modals/AssignComponentsToSet/locales/${lang}.json`,
  [TranslationNamespaceEnum.DeleteRecordConfirmationModal]: (lang: string) =>
    `src/admin/modals/DeleteRecordConfirmation/locales/${lang}.json`,
  [TranslationNamespaceEnum.EditAttributeValueModal]: (lang: string) =>
    `src/admin/modals/attribute/EditAttributeValue/locales/${lang}.json`,
  [TranslationNamespaceEnum.CreateAttributeValueModal]: (lang: string) =>
    `src/admin/modals/attribute/CreateAttributeValue/locales/${lang}.json`,
  [TranslationNamespaceEnum.DeleteAttributeValueModal]: (lang: string) =>
    `src/admin/modals/attribute/DeleteAttributeValue/locales/${lang}.json`,
  [TranslationNamespaceEnum.FormContactsPanel]: (lang: string) =>
    `src/components/Form/panels/ContactsPanel/locales/${lang}.json`,
  [TranslationNamespaceEnum.SetComponentList]: (lang: string) =>
    `src/admin/modules/product/components/SetComponents/locales/${lang}.json`,
  [TranslationNamespaceEnum.ProductsImport]: (lang: string) =>
    `src/admin/import/form/${lang}.json`,
  [TranslationNamespaceEnum.B2BGateEmptyScreen]: (lang: string) =>
    `src/admin/layout/B2BGateEmptyScreen/${lang}.json`,
  [TranslationNamespaceEnum.VoucherAndGeneration]: (lang: string) =>
    `src/admin/modules/voucher-serie/routes/edit/tabs/vouchers-and-generation/vouchers-and-generation.${lang}.json`,
  [TranslationNamespaceEnum.DiscountGiftItemTable]: (lang: string) =>
    `src/admin/forms/discount/shared/views/GiftItemTable/GiftItemTable.${lang}.json`,
  [TranslationNamespaceEnum.EditWarehousePlacesTab]: (lang: string) =>
    `src/admin/modules/warehouse/routes/edit/tabs/storage-scheme/storage-scheme.${lang}.json`,
  [TranslationNamespaceEnum.UpdateRequiredModal]: (lang: string) =>
    `src/admin/modals/UpdateRequiredModal/locales/${lang}.json`,
};
